/**
 * API and search state types for CarolinaShop Live Search
 *
 * This file contains TypeScript types for API communication and search state management.
 */

import { SearchResults } from "../search-core/types/SearchResults";
import { SearchHistoryItem } from "../search-core/history";

// Search status types as required in the task
export type SearchStatus =
  | "idle" // пустая строка поиска
  | "typing" // пользователь вводит текст (debounce активен)
  | "loading" // запрос выполняется
  | "success" // получен ответ с результатами
  | "empty" // нет результатов
  | "error"; // ошибка сети или сервера

// Constants as required in the task
export const MIN_QUERY_LENGTH = 2;
export const DEBOUNCE_MS = 350; // 300-400 ms as specified

// Meilisearch API request body (what we send to /api/search)
// Based on Meilisearch documentation: https://www.meilisearch.com/docs/reference/api/search
export interface MeilisearchSearchRequest {
  q: string; // Query string
  limit?: number; // Maximum number of results to return
  offset?: number; // Number of documents to skip
  filter?: unknown; // Filter queries
  sort?: string[]; // Sorting criteria
  facets?: string[]; // Facets to retrieve
  attributesToRetrieve?: string[]; // Attributes to retrieve
  attributesToCrop?: string[]; // Attributes to crop
  cropLength?: number; // Length to crop attributes to
  attributesToHighlight?: string[]; // Attributes to highlight
  highlightPreTag?: string; // HTML tag to insert before highlighted terms
  highlightPostTag?: string; // HTML tag to insert after highlighted terms
  showMatchesPosition?: boolean; // Whether to return matches position
  matchingStrategy?: "last" | "all"; // Matching strategy
  hitsPerPage?: number; // Number of hits per page (deprecated, use limit)
  page?: number; // Page number (deprecated, use offset)
}

// Meilisearch API response (raw response from proxy)
// Based on Meilisearch documentation: https://www.meilisearch.com/docs/reference/api/search#response
export interface MeilisearchSearchResponse {
  hits: unknown[]; // Array of search results (the actual documents)
  query: string; // Query string
  processingTimeMs: number; // Time taken by Meilisearch to process the request
  limit: number; // Number of results returned
  offset: number; // Offset used for pagination
  estimatedTotalHits: number; // Estimated total number of matching documents
  facets?: unknown; // Facet distribution if requested
  facetStats?: unknown; // Facet statistics if requested
  totalHits?: number; // Total number of matching documents (deprecated, use estimatedTotalHits)
  hitsPerPage?: number; // Number of hits per page (deprecated)
  page?: number; // Page number (deprecated)
  totalPages?: number; // Total number of pages (deprecated)
}

// Error response from the proxy server
export interface ProxyErrorResponse {
  error: string;
  message?: string;
}

// Configuration for API search service
export interface ApiSearchServiceConfig {
  baseUrl: string; // Base URL for the proxy server (e.g., 'http://localhost:3001')
  timeout?: number; // Request timeout in milliseconds
  headers?: Record<string, string>; // Additional headers
}

// Return type for useLiveSearch hook
export interface UseLiveSearchReturn {
  // State
  query: string;
  results: unknown[]; // Minimal results (hits as is) - legacy, temporary
  status: SearchStatus;
  error: string | null;
  rawResponse: MeilisearchSearchResponse | null;
  normalizedResults: SearchResults | null; // Normalized results for UI consumption

  // Search history
  history: SearchHistoryItem[]; // All search history items
  filteredHistory: SearchHistoryItem[]; // History filtered by current query
  suggestions: string[]; // Search suggestions (strings only)

  // Actions
  setQuery: (query: string) => void;
  search: (query: string) => Promise<void>;
  cancel: () => void;
  clear: () => void;
  commitSearch: () => boolean;

  loadMore: () => void;
  hasMore: boolean;

  // Derived state
  isLoading: boolean;
  isSuccess: boolean;
  isEmpty: boolean;
  hasError: boolean;
}

// Cart API types
export interface CartAddRequest {
  /** Bitrix element ID or 1C code (KODTOVARA1S) */
  id: number | string;
  quantity: number;
}

export interface CartAddResponse {
  success: boolean;
  message?: string;
  productId?: number;
  quantity?: number;
  error?: string;
}

export interface ApiCartServiceConfig {
  baseUrl: string;
  timeout?: number;
  headers?: Record<string, string>;
}
